import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Loader2, Clock, ChevronDown, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RANGES = [{ d: 7, l: '7d' }, { d: 30, l: '30d' }, { d: 90, l: '90d' }, { d: 0, l: 'All' }];
const ROLES = ['all', 'job_seeker', 'employer', 'regional_admin', 'admin'];

const VERB = {
  user_registered: 'Registered', user_login: 'Logged in', session_ended: 'Logged out',
  resume_updated: 'Updated resume', resume_completed: 'Completed resume', profile_visibility_enabled: 'Made profile visible',
  opportunity_viewed: 'Viewed an opportunity', opportunity_saved: 'Saved an opportunity',
  opportunities_viewed: 'Viewed opportunities list', opportunity_search: 'Searched opportunities',
  opportunity_filter_changed: 'Changed filters', opportunity_sort_changed: 'Changed sort',
  application_started: 'Started an application', application_submitted: 'Submitted an application',
  application_contacted: 'Contacted a provider', application_withdrawn: 'Withdrew an application',
  application_hired: 'Marked candidate hired', job_published: 'Published a job',
  resume_access_requested: 'Requested resume access', application_reviewed: 'Reviewed an application',
  conversation_started: 'Started a conversation',
};
const fmtT = (t) => t ? new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
const fmtD = (t) => t ? new Date(t).toLocaleString([], { month: 'short', day: 'numeric' }) : '';

export default function AdminSessions() {
  const [days, setDays] = useState(30);
  const [role, setRole] = useState('all');
  const [eventName, setEventName] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  useEffect(() => {
    let active = true; setLoading(true);
    const p = new URLSearchParams({ days: String(days) });
    if (role !== 'all') p.append('role', role);
    if (eventName !== 'all') p.append('event_name', eventName);
    axios.get(`${BACKEND_URL}/api/admin/sessions?${p.toString()}`, { withCredentials: true })
      .then(r => active && setData(r.data)).catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [days, role, eventName]);

  return (
    <div className="space-y-4" data-testid="admin-sessions">
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="sessions-role-filter" value={role} onChange={e => setRole(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
          {ROLES.map(r => <option key={r} value={r}>{r === 'all' ? 'All roles' : r}</option>)}
        </select>
        <select data-testid="sessions-event-filter" value={eventName} onChange={e => setEventName(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
          <option value="all">All events</option>
          {Object.keys(VERB).map(k => <option key={k} value={k}>{VERB[k]}</option>)}
        </select>
        <div className="flex gap-1 ml-auto">
          {RANGES.map(r => <button key={r.l} onClick={() => setDays(r.d)} className={`px-2 py-1 rounded text-xs ${days === r.d ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{r.l}</button>)}
        </div>
      </div>
      <p className="text-xs text-slate-400">Activity sessions are derived from product events; a gap of more than 30 minutes starts a new session.</p>

      {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
        : !data || data.session_count === 0 ? <Card className="p-8 text-center text-slate-500">No activity sessions yet for this filter.</Card>
        : (
          <div className="space-y-2">
            {data.sessions.map((s, i) => {
              const id = `${s.user_id}-${s.start}`;
              const isOpen = open[id];
              return (
                <Card key={id} className="border border-slate-200" data-testid={`session-row-${i}`}>
                  <button onClick={() => setOpen(o => ({ ...o, [id]: !o[id] }))} className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{s.actor_name || 'Unknown'} <span className="text-xs font-normal text-slate-400">{s.actor_role}</span></p>
                      <p className="text-xs text-slate-500">{fmtD(s.start)}, {fmtT(s.start)} – {fmtT(s.end)}</p>
                    </div>
                    <div className="text-right text-xs text-slate-500 whitespace-nowrap"><Clock className="w-3 h-3 inline mr-1" />{s.duration_minutes}m · {s.event_count} events</div>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-4 pl-12" data-testid={`session-timeline-${i}`}>
                      {s.events.map((e, j) => (
                        <div key={j} className="flex gap-3 py-1 text-sm border-l-2 border-slate-100 pl-3">
                          <span className="text-slate-400 text-xs w-16 flex-shrink-0">{fmtT(e.timestamp)}</span>
                          <span className="text-slate-700">{VERB[e.event_name] || e.event_name}{e.metadata?.opportunity_type ? ` · ${e.metadata.opportunity_type}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}
